export interface LogoEntryOffsetState {
  elapsedMs: number;
  durationMs: number;
  index: number;
  /** 0 is the top of the source logo, 1 is the bottom. */
  normalizedY: number;
}

export interface SceneWaitPulseState {
  elapsedSeconds: number;
  radius: number;
  index: number;
  dirX: number;
  dirZ: number;
}

export interface CameraLookGateState {
  worldReady: boolean;
  entranceActive: boolean;
  isPlacingMeasure: boolean;
}

export interface CameraMoveGateState {
  worldReady: boolean;
  isPlacingMeasure: boolean;
}

const LOGO_ENTRY_DROP_WORLD = 1.45;
const LOGO_ROW_STAGGER = 0.22;
const LOGO_POINT_STAGGER = 0.08;
const SCENE_PULSE_VERTICAL = 0.05;
const SCENE_PULSE_RADIAL = 0.012;

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function easeOutCubic(t: number): number {
  const p = 1 - clamp01(t);
  return 1 - p * p * p;
}

function stableJitter(index: number): number {
  return Math.sin((index + 1) * 12.9898) * 43758.5453 % 1;
}

export function logoEntryOffsetY(state: LogoEntryOffsetState): number {
  const duration = Math.max(1, state.durationMs);
  const t = clamp01(state.elapsedMs / duration);
  if (t >= 1) return 0;

  const rowDelay = clamp01(state.normalizedY) * LOGO_ROW_STAGGER;
  const jitter = Math.abs(stableJitter(state.index)) * LOGO_POINT_STAGGER;
  const delay = Math.min(0.42, rowDelay + jitter);
  const localT = clamp01((t - delay) / Math.max(0.001, 1 - delay));
  return LOGO_ENTRY_DROP_WORLD * (1 - easeOutCubic(localT));
}

export function sceneWaitPulseOffset(state: SceneWaitPulseState): { x: number; y: number; z: number } {
  const primary = Math.sin(state.radius * 0.82 - state.elapsedSeconds * 1.85);
  const secondary = Math.sin(state.elapsedSeconds * 0.7 + state.index * 0.31) * 0.28;
  const y = (primary * 0.72 + secondary * 0.28) * SCENE_PULSE_VERTICAL;
  const radial =
    Math.sin(state.radius * 0.58 - state.elapsedSeconds * 1.55 + state.index * 0.07) *
    SCENE_PULSE_RADIAL;
  return {
    x: state.dirX * radial,
    y,
    z: state.dirZ * radial,
  };
}

export function canUpdateCameraLook(state: CameraLookGateState): boolean {
  if (state.isPlacingMeasure) return false;
  return state.worldReady || state.entranceActive;
}

export function canMoveCamera(state: CameraMoveGateState): boolean {
  return state.worldReady && !state.isPlacingMeasure;
}
